import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { notifications } from "@mantine/notifications"
import { createFileRoute } from "@tanstack/react-router"
import { useCallback, useMemo, useState } from "react"
import { useCreditStatement, useCreditTransactions, useCreditWallet } from "../../hooks/useCredits"
import { extractErrorMessage } from "../../lib/api/errors"
import { CreditsView } from "./-usage/CreditsView"
import { getDateRange, type TDateRange } from "./-usage/date-range"

export const Route = createFileRoute("/_auth/usage")({
  component: CreditsPage,
})

/** Enough ledger history to merge with a month of meetings. The view pages it. */
const LEDGER_FETCH_LIMIT = 200

/**
 * Tenant billing surface — CREDITS ONLY. The USD usage view this route used to
 * render is gone; tenants now see their credit wallet, where their credits went,
 * and the credit ledger. The USD `/usage` API + super-admin cost views are
 * unaffected — this is only the tenant-facing projection.
 */
function CreditsPage(): React.JSX.Element {
  const { i18n } = useLingui()
  const [range, setRange] = useState<TDateRange>("mtd")
  const [page, setPage] = useState<number>(1)
  const { from, to } = useMemo(() => getDateRange(range), [range])

  const walletQuery = useCreditWallet()
  const statementQuery = useCreditStatement({ from, to })
  const transactionsQuery = useCreditTransactions({ limit: LEDGER_FETCH_LIMIT, offset: 0 })

  const handleRangeChange = useCallback((next: TDateRange): void => {
    setRange(next)
    setPage(1)
  }, [])

  const handlePageChange = useCallback((next: number): void => {
    setPage(next)
  }, [])

  // Retry every credits query — a failed section's "Try again" refetches all
  // three; healthy ones are cheap no-ops.
  const handleRetry = useCallback((): void => {
    void walletQuery.refetch()
    void statementQuery.refetch()
    void transactionsQuery.refetch()
  }, [walletQuery, statementQuery, transactionsQuery])

  // TODO(credits Phase 6): wire to the real checkout once a payment provider
  // (Mollie / Stripe) is picked. For now this is an explicit "coming soon" stub —
  // no checkout is built here.
  const handleBuyCredits = useCallback((): void => {
    notifications.show({
      title: i18n._(msg`Buying credits is coming soon`),
      message: i18n._(msg`Self-service top-ups land in a future update. Reach out if you need more credits now.`),
      color: "info",
    })
  }, [i18n])

  return (
    <CreditsView
      wallet={walletQuery.data ?? null}
      enforcementEnabled={walletQuery.data?.enforcementEnabled ?? false}
      statement={statementQuery.data ?? null}
      transactions={transactionsQuery.data ?? null}
      isLoading={walletQuery.isLoading || transactionsQuery.isFetching || statementQuery.isFetching}
      walletError={walletQuery.isError ? extractErrorMessage(walletQuery.error) : null}
      statementError={statementQuery.isError ? extractErrorMessage(statementQuery.error) : null}
      transactionsError={transactionsQuery.isError ? extractErrorMessage(transactionsQuery.error) : null}
      onRetry={handleRetry}
      range={range}
      periodFrom={from}
      periodTo={to}
      onRangeChange={handleRangeChange}
      onBuyCredits={handleBuyCredits}
      activityPage={page}
      onTransactionsPageChange={handlePageChange}
    />
  )
}
